// client/src/components/pages/Settings/LexiStarAssets/AssetForm.jsx
//
// Create / edit an asset in the LexiStar register (ST-00188, T3-S01). Modal in
// the LocationOwnership style. Validates the same rules the API enforces
// (server/modules/lexistar/fleet/assetsValidation.js) so most mistakes are
// caught before a round trip; the server's message is shown when it still
// refuses (e.g. 409 duplicate registration).

import React, { useState, useEffect } from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';

export const ASSET_TYPE_LABELS = {
  horse: 'Truck tractor (horse)',
  rigid: 'Rigid truck',
  trailer: 'Trailer',
  ldv: 'LDV',
  loader: 'Loader',
  plant: 'Plant',
};

export const ASSET_STATUS_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  maintenance: 'In maintenance',
  decommissioned: 'Decommissioned',
};

const TEXT_FIELDS = [
  'registration',
  'fleet_no',
  'vin',
  'vehicle_class',
  'make',
  'model',
  'axle_config',
];
const NUMBER_FIELDS = ['year', 'tare_kg', 'gvm_kg', 'gcm_kg', 'tank_capacity_l'];

const EMPTY = {
  registration: '',
  asset_type: 'horse',
  status: 'active',
  fleet_no: '',
  vin: '',
  vehicle_class: '',
  make: '',
  model: '',
  year: '',
  axle_config: '',
  tare_kg: '',
  gvm_kg: '',
  gcm_kg: '',
  tank_capacity_l: '',
};

const inputClasses =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

/** Same rule as lexistar.normalise_registration(): something left after keeping A-Z/0-9. */
export const hasRegistrationChars = (v) => /[0-9A-Za-z]/.test(v || '');

const toForm = (asset) => {
  if (!asset) return { ...EMPTY };
  const f = { ...EMPTY };
  for (const k of Object.keys(EMPTY)) {
    f[k] = asset[k] === null || asset[k] === undefined ? EMPTY[k] : String(asset[k]);
  }
  return f;
};

/** Field-level errors, keyed by field. Empty object = valid. */
export function validateAssetForm(form) {
  const errors = {};
  const reg = form.registration.trim();
  if (!reg) errors.registration = 'Registration is required';
  else if (!hasRegistrationChars(reg)) errors.registration = 'Registration needs letters or digits';
  else if (reg.length > 32) errors.registration = 'At most 32 characters';

  if (form.year !== '') {
    const y = Number(form.year);
    if (!Number.isInteger(y) || y < 1900 || y > 2100) errors.year = 'A year between 1900 and 2100';
  }
  for (const k of ['tare_kg', 'gvm_kg', 'gcm_kg']) {
    if (form[k] !== '') {
      const n = Number(form[k]);
      if (!Number.isInteger(n) || n <= 0 || n > 1000000) errors[k] = 'Whole kilograms, above 0';
    }
  }
  if (form.tank_capacity_l !== '') {
    const n = Number(form.tank_capacity_l);
    if (!Number.isFinite(n) || n <= 0 || n > 9999999.9) errors.tank_capacity_l = 'Litres, above 0';
  }
  return errors;
}

/** Form state -> API payload. For an edit, only the fields that changed (+ version). */
function toPayload(form, original) {
  const out = {};
  const put = (k, v) => {
    const before = original ? (original[k] === undefined ? null : original[k]) : undefined;
    if (!original || String(before ?? '') !== String(v ?? '')) out[k] = v;
  };
  for (const k of TEXT_FIELDS) {
    const v = form[k].trim();
    put(k, k === 'registration' ? v : v || null);
  }
  for (const k of NUMBER_FIELDS) put(k, form[k] === '' ? null : Number(form[k]));
  put('asset_type', form.asset_type);
  put('status', form.status);
  if (original && Object.keys(out).length > 0) out.version = original.version;
  return out;
}

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const AssetForm = ({ isOpen, asset, onClose, onSave }) => {
  const isEdit = !!asset;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(toForm(asset));
      setErrors({});
      setServerError(null);
    }
  }, [isOpen, asset]);

  if (!isOpen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validateAssetForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const payload = toPayload(form, asset);
    if (isEdit && Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setServerError(null);
    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setServerError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const text = (k, label, props = {}) => (
    <Field label={label} error={errors[k]} required={props.required}>
      <input
        type="text"
        value={form[k]}
        onChange={set(k)}
        className={`${inputClasses} ${props.mono ? 'font-mono' : ''}`}
        placeholder={props.placeholder}
        aria-label={label}
        maxLength={props.maxLength}
      />
    </Field>
  );

  const number = (k, label, props = {}) => (
    <Field label={label} error={errors[k]}>
      <input
        type="number"
        inputMode={props.decimal ? 'decimal' : 'numeric'}
        step={props.decimal ? '0.1' : '1'}
        min="0"
        value={form[k]}
        onChange={set(k)}
        className={inputClasses}
        placeholder={props.placeholder}
        aria-label={label}
      />
    </Field>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        noValidate
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? `Edit asset ${asset.registration}` : 'New asset'}
        className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">
            {isEdit ? `Edit ${asset.registration}` : 'New asset'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {serverError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <FaExclamationTriangle className="text-red-400 mt-0.5 flex-shrink-0" size={14} />
              <p className="text-sm text-red-600">{serverError}</p>
            </div>
          )}

          <section>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Identity
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {text('registration', 'Registration', {
                required: true,
                mono: true,
                placeholder: 'e.g. JX 12 KL GP',
                maxLength: 32,
              })}
              {text('fleet_no', 'Fleet no', { placeholder: 'e.g. T-014', maxLength: 32 })}
              <Field label="Type">
                <ThemedSelect
                  value={form.asset_type}
                  onChange={set('asset_type')}
                  className={inputClasses}
                  aria-label="Type"
                >
                  {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </ThemedSelect>
              </Field>
              <Field label="Status">
                <ThemedSelect
                  value={form.status}
                  onChange={set('status')}
                  className={inputClasses}
                  aria-label="Status"
                >
                  {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </ThemedSelect>
              </Field>
              {text('vehicle_class', 'Class', { placeholder: 'e.g. truck', maxLength: 40 })}
              {text('vin', 'VIN', { mono: true, maxLength: 32 })}
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Specification
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {text('make', 'Make', { maxLength: 60 })}
              {text('model', 'Model', { maxLength: 60 })}
              {number('year', 'Year', { placeholder: 'e.g. 2021' })}
              {text('axle_config', 'Axle configuration', {
                placeholder: 'e.g. 6x4',
                maxLength: 20,
              })}
              {number('tare_kg', 'Tare (kg)')}
              {number('gvm_kg', 'GVM (kg)')}
              {number('gcm_kg', 'GCM (kg)')}
              {number('tank_capacity_l', 'Tank capacity (L)', { decimal: true })}
            </div>
          </section>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow ${saving ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create asset'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssetForm;
